/**
 * Status values for KapchargeTransaction.
 *
 * Lifecycle:
 *   Invalid       : uploaded with invalid/incomplete info, or the scheduled payment
 *                   could not be resolved; NOT sent to KapCharge until corrected
 *                   (pre-Queued, platform-managed)
 *   Queued        : queued for submission to KapCharge
 *   Initiated     : claimed by a submit-cron run, about to be sent to KapCharge
 *                   (transient; prevents a row from being picked twice)
 *   Error         : failure on our side (NLS / KapCharge submit / etc.)
 *   KapCharge webhook updates the status to one of:
 *     Processing, Accepted, Cancelled, Returned, TxnError
 */
export enum KapchargeTransactionStatus {
	// Platform-managed
	Invalid = 'Invalid',
	Queued = 'Queued',
	Initiated = 'Initiated',
	Error = 'Error',

	// KapCharge webhook-managed (Processing is also set by us on submit)
	Processing = 'Processing',
	Accepted = 'Accepted',
	Cancelled = 'Cancelled',
	Returned = 'Returned',
	TxnError = 'TxnError',
}

export const KAPCHARGE_TRANSACTION_STATUSES: KapchargeTransactionStatus[] = [
	KapchargeTransactionStatus.Invalid,
	KapchargeTransactionStatus.Queued,
	KapchargeTransactionStatus.Initiated,
	KapchargeTransactionStatus.Error,
	KapchargeTransactionStatus.Processing,
	KapchargeTransactionStatus.Accepted,
	KapchargeTransactionStatus.Cancelled,
	KapchargeTransactionStatus.Returned,
	KapchargeTransactionStatus.TxnError,
];

/**
 * The two error states, shown to the user as a single "Error" bucket: a failure on our
 * side and KapCharge's TxnError describe the same outcome to an operator. Mirrors
 * ERROR_STATUSES in the backend (utils/payment-processing/kapcharge-transaction-status.js),
 * which groups them in both the batch summary's errorCount and the status filter.
 */
export const KAPCHARGE_ERROR_STATUSES: KapchargeTransactionStatus[] = [
	KapchargeTransactionStatus.Error,
	KapchargeTransactionStatus.TxnError,
];

/**
 * Statuses offered by the transaction status filter. TxnError is deliberately absent:
 * Error already covers it (the backend expands Error to KAPCHARGE_ERROR_STATUSES), so
 * offering it separately would split a bucket the summary presents as one.
 */
export const KAPCHARGE_STATUS_FILTER_OPTIONS: KapchargeTransactionStatus[] =
	KAPCHARGE_TRANSACTION_STATUSES.filter((status) => status !== KapchargeTransactionStatus.TxnError);

/**
 * Per-status colors, drawn from the LMC palette (_variables.scss). Single source
 * of truth shared by the status-breakdown legend (via KAPCHARGE_STATUS_BREAKDOWN)
 * and the table status badges, so a given status shows the same hue everywhere.
 */
export const KAPCHARGE_STATUS_COLORS: Readonly<Record<KapchargeTransactionStatus, string>> = {
	[KapchargeTransactionStatus.Queued]: '#7986cb', // primary-300
	[KapchargeTransactionStatus.Initiated]: '#4351AF', // primary-500
	[KapchargeTransactionStatus.Processing]: '#ff9800', // warning-500
	[KapchargeTransactionStatus.Accepted]: '#4caf50', // success-500
	[KapchargeTransactionStatus.Returned]: '#e57373', // error-300
	[KapchargeTransactionStatus.Cancelled]: '#9e9e9e', // neutral
	[KapchargeTransactionStatus.Invalid]: '#f57c00', // warning-700
	[KapchargeTransactionStatus.Error]: '#d32f2f', // error-700
	[KapchargeTransactionStatus.TxnError]: '#d32f2f', // error-700 (grouped with Error)
};

/**
 * Statuses considered a failure. Mirrors FAILURE_STATUSES in the backend
 * (utils/kapcharge-transaction-status.js). These are the only statuses a
 * transaction may be retried from.
 */
export const KAPCHARGE_FAILURE_STATUSES: KapchargeTransactionStatus[] = [
	KapchargeTransactionStatus.Cancelled,
	KapchargeTransactionStatus.Returned,
	KapchargeTransactionStatus.TxnError,
	KapchargeTransactionStatus.Error,
];

/**
 * Statuses eligible for a refund. Mirrors REFUNDABLE_STATUSES in the backend
 * (utils/kapcharge-transaction-status.js).
 */
export const KAPCHARGE_REFUNDABLE_STATUSES: KapchargeTransactionStatus[] = [
	KapchargeTransactionStatus.Processing,
	KapchargeTransactionStatus.Accepted,
];

/**
 * Statuses eligible to be cancelled — anything not yet sent to KapCharge. Mirrors
 * CANCELLABLE_STATUSES in the backend (utils/kapcharge-transaction-status.js).
 */
export const KAPCHARGE_CANCELLABLE_STATUSES: KapchargeTransactionStatus[] = [
	KapchargeTransactionStatus.Queued,
	KapchargeTransactionStatus.Invalid,
	KapchargeTransactionStatus.Error,
];

/**
 * Statuses whose details may still be corrected in place — anything not yet sent to
 * KapCharge. Same membership as KAPCHARGE_CANCELLABLE_STATUSES today, kept separate so
 * restricting what is editable never silently changes what is cancellable.
 */
export const KAPCHARGE_EDITABLE_STATUSES: KapchargeTransactionStatus[] = [
	KapchargeTransactionStatus.Queued,
	KapchargeTransactionStatus.Invalid,
	KapchargeTransactionStatus.Error,
];

/**
 * Values for KapchargeTransaction.transactionFlow. Mirrors KapchargeTransactionFlow
 * in the backend (utils/kapcharge-transaction-status.js).
 */
export enum KapchargeTransactionFlow {
	Collect = 'Collect',
	Pay = 'Pay',
}

export namespace KapchargeTransactionStatus {
	/**
	 * Resolves the badge color for a status from the shared palette
	 * (KAPCHARGE_STATUS_COLORS) — the same colors used by the status-breakdown
	 * legend. Bind it to the badge's `--status-color` custom property; the SCSS
	 * derives the pill's tinted background and text from that one value. Unknown
	 * statuses fall back to the neutral (Cancelled) color.
	 */
	export function getColor(status: string | null | undefined): string {
		return (
			KAPCHARGE_STATUS_COLORS[status as KapchargeTransactionStatus] ??
			KAPCHARGE_STATUS_COLORS[KapchargeTransactionStatus.Cancelled]
		);
	}
}